import {
  ampMult,
  ampRxnTrigElmts,
  hitBnTypes,
  bnFields,
  reactions,
  tfmRxns
} from "../configs";
import { findArtifact, findCharacter, findWeapon } from "../data/controllers";
import { findByIndex, toMult } from "../helpers";
import { getAcro } from "../library";
import {
  addArtBonuses,
  addArtStats,
  addWpBonuses,
  addWpSubStat,
  calcFinalATTRs,
  initiateATTRs
} from "./baseStats";
import {
  addAndTrack,
  getQuickenBuffDamage,
  getRxnBnesFromEM,
  incElementalBnes,
  pushOrMerge
} from "./helpers";

function addCustomBuffs({ ATTRs, hitBnes, rxnBnes, tracker }, BCs) {
  for (const { catKey, type, val } of BCs) {
    const desc = "Custom Buff";
    if (catKey === 2) {
      const thisType = getAcro(type);
      hitBnes[thisType].pct += val;
      pushOrMerge(tracker?.[thisType], "pct", desc, val);
    } else {
      (catKey < 2 ? ATTRs : rxnBnes)[type] += val;
      pushOrMerge(tracker, type, desc, val);
    }
  }
}

function addResonanceBuffs(ATTRs, hitBnes, resonance, tracker) {
  for (const rsn of resonance) {
    if (rsn.activated) {
      let { field, value, elmt } = {
        "Sprawling Greenery": {
          field: "Elemental Mastery",
          value: 50,
          elmt: "Dendro"
        },
        "Fervent Flames": { field: "ATK%", value: 25, elmt: "Pyro" },
        "Shattering Ice": { field: "CRIT Rate", value: 15, elmt: "Cryo" },
        "Enduring Rock": { field: "Shield Strength", value: 15, elmt: "Geo" },
        "Soothing Water": { field: "HP%", value: 25, elmt: "Hydro" }
      }[rsn.name];

      if (elmt === "Dendro") {
        if (rsn.inputs[0]) {
          value += 30;
        }
        if (rsn.inputs[1]) {
          value += 20;
        }
      }
      const desc = `${elmt} Resonance`;
      addAndTrack(desc, ATTRs, field, value, tracker);
      if (rsn.name === "Enduring Rock") {
        addAndTrack(desc, hitBnes, "All.pct", 15, tracker);
      }
    }
  }
}

function addWpBuffs(wrapper, weaponBCs, wpRfm, subWpBCs, wpData) {
  for (const type in subWpBCs) {
    for (const ctrl of subWpBCs[type]) {
      if (ctrl.activated) {
        const { code, refinement, inputs } = ctrl;
        const { name, buffs } = findWeapon({ type, code });
        const { addBnes } = findByIndex(buffs, ctrl.index);
        const tkDesc = `${name} activated`;
        addBnes({ ...wrapper, wpRfm: refinement, inputs, tkDesc });
      }
    }
  }
  for (const { activated, index, inputs } of weaponBCs) {
    const { addBnes } = findByIndex(wpData.buffs, index);
    if (activated && addBnes) {
      const tkDesc = `${wpData.name} activated`;
      addBnes({ ...wrapper, wpRfm, inputs, tkDesc });
    }
  }
}

function addArtBuffs(wrapper, art) {
  for (const { activated, code, index, inputs } of art.subBCs) {
    if (activated) {
      const { name, buffs } = findArtifact({ code });
      const tkDesc = `${name} / 4-Piece activated`;
      buffs[index].addBnes({ ...wrapper, inputs, tkDesc });
    }
  }
  for (const { index, activated, inputs } of art.BCs) {
    const { name, buffs } = findArtifact({ code: art.sets[0].code });
    const { addBnes } = buffs[index];
    if (activated && addBnes) {
      const tkDesc = `${name} (self) / 4-Piece activated`;
      addBnes({ ...wrapper, inputs, tkDesc });
    }
  }
}

function addTeammateBuffs(isFinal, party, partyData, wrapper, char, infusion) {
  for (const tm of party.filter((m) => m)) {
    const { buffs } = findCharacter(tm);
    for (const ctrl of tm.BCs) {
      const { index, activated, inputs } = ctrl;
      const buff = findByIndex(buffs, index);
      if (!buff) continue;
      const fn =
        isFinal && buff.addFinalBnes
          ? buff.addFinalBnes
          : !isFinal && buff.addBnes
          ? buff.addBnes
          : undefined;
      if (activated && fn) {
        const tkDesc = `${tm.name} / ${buff.src}`;
        const wrapper2 = { char, inputs, infusion, party, partyData, tkDesc };
        fn({ ...wrapper, ...wrapper2, toSelf: false, charBCs: tm.BCs });
      }
    }
  }
}

function addWpFinalBuffs(ATTRs, hitBnes, weapon, wpData, tracker) {
  for (let ctrl of weapon.BCs) {
    if (ctrl.activated) {
      const { addFinalBnes } = findByIndex(wpData.buffs, ctrl.index);
      if (addFinalBnes) {
        const tkDesc = `${wpData.name} activated`;
        addFinalBnes({
          ATTRs,
          hitBnes,
          wpRfm: weapon.refinement,
          inputs: ctrl.inputs,
          tkDesc,
          tracker
        });
      }
    }
  }
}

function addArtFinalBuffs(ATTRs, hitBnes, art, tracker) {
  for (const ctrl of art.BCs) {
    const { name, buffs } = findArtifact({ code: art.sets[0].code });
    const { addFinalBnes } = buffs[ctrl.index];
    if (ctrl.activated && addFinalBnes) {
      const tkDesc = `${name} (self) / 4-Piece activated`;
      addFinalBnes({ ATTRs, hitBnes, tkDesc, tracker });
    }
  }
}

function addSelfBuffs(isFinal, wrapper, wrapper2, partyData) {
  const { char, charBCs } = wrapper2;
  const { buffs } = findCharacter(char);
  for (const { index, activated, inputs } of charBCs) {
    const buff = findByIndex(buffs, index);
    if (buff && buff.isGranted(char) && activated) {
      let fn = null;
      if (!isFinal && buff.addBnes) fn = buff.addBnes;
      else if (isFinal && buff.addFinalBnes) fn = buff.addFinalBnes;
      if (!fn) continue;
      const tkDesc = `Self / ${buff.src}`;
      fn({ ...wrapper, ...wrapper2, partyData, inputs, toSelf: true, tkDesc });
    }
  }
}

function calcFinalRxnBnes(ATTRs, rxnBnes, vision, infusion) {
  const { transformative, amplifying } = getRxnBnesFromEM(
    ATTRs["Elemental Mastery"]
  );
  Object.keys(tfmRxns).forEach((rxn) => {
    rxnBnes[rxn] += transformative;
  });
  Object.keys(ampRxnTrigElmts).forEach((rxn) => {
    rxnBnes[rxn] += amplifying;
  });
  const meltBn = toMult(rxnBnes.Melt);
  const vapBn = toMult(rxnBnes.Vaporize);
  rxnBnes.Melt = ampMult.melt(vision) * meltBn;
  rxnBnes.Vaporize = ampMult.vaporize(vision) * vapBn;
  if (infusion.NA !== vision) {
    rxnBnes.naMelt = ampMult.melt(infusion.NA) * meltBn;
    rxnBnes.naVaporize = ampMult.vaporize(infusion.NA) * vapBn;
  }
}

export default function getBuffedStats(
  char,
  charData,
  charBCs,
  weapon,
  subWpBCs,
  art,
  elmtMCs,
  party,
  partyData,
  customBCs,
  infusion,
  tracker
) {
  const wpData = findWeapon(weapon);
  const ATTRs = initiateATTRs(char, wpData, weapon, tracker);
  const artSBnes = addArtStats(art.pieces, ATTRs, tracker);
  addWpSubStat(wpData, ATTRs, weapon.level, tracker);

  const hitBnes = {};
  for (const bnType of hitBnTypes) {
    hitBnes[bnType] = {};
    for (const field of bnFields) {
      hitBnes[bnType][field] = 0;
    }
  }
  const rxnBnes = {};
  for (const rxn of reactions) {
    rxnBnes[rxn] = 0;
  }
  const wrapper = { ATTRs, hitBnes, rxnBnes, charData, partyData, tracker };

  const wpRfm = weapon.refinement;
  addWpBonuses(false, wpData, wpRfm, wrapper, partyData);
  addArtBonuses(false, art.sets, wrapper);
  // charData is not needed
  addCustomBuffs(wrapper, customBCs);
  addResonanceBuffs(ATTRs, hitBnes, elmtMCs.resonance, tracker);

  // rxnBnes is not needed yet
  addWpBuffs(wrapper, weapon.BCs, wpRfm, subWpBCs, wpData);
  addArtBuffs(wrapper, art);
  addTeammateBuffs(false, party, partyData, wrapper, char, infusion);
  const wrapper2 = { char, charBCs, infusion, party };
  // charData is not needed yet
  addSelfBuffs(false, wrapper, wrapper2, partyData);

  calcFinalATTRs(ATTRs);
  addArtBonuses(true, art.sets, wrapper);
  addWpBonuses(true, wpData, wpRfm, wrapper, partyData);
  addWpFinalBuffs(ATTRs, hitBnes, weapon, wpData, tracker);
  addSelfBuffs(true, wrapper, wrapper2, partyData);
  addArtFinalBuffs(ATTRs, hitBnes, art, tracker);
  addTeammateBuffs(true, party, partyData, wrapper, char, infusion);

  // add quicken reaction buff
  const quickenBuff = getQuickenBuffDamage(
    char,
    ATTRs["Elemental Mastery"],
    rxnBnes
  );
  if (elmtMCs.spread) {
    const settings = {
      vision: charData.vision,
      infusion,
      elmt: "Dendro",
      type: "flat",
      value: quickenBuff.spread
    };
    incElementalBnes(settings, hitBnes, "Spread reaction", tracker);
  }
  if (elmtMCs.aggravate && charData.vision === "Electro") {
    const settings = {
      vision: charData.vision,
      infusion,
      elmt: "Electro",
      type: "flat",
      value: quickenBuff.aggravate
    };
    incElementalBnes(settings, hitBnes, "Aggravate reaction", tracker);
  }

  hitBnes.Elemental.pct += ATTRs[charData.vision + " DMG Bonus"];
  hitBnes.Physical.pct += ATTRs["Physical DMG Bonus"];
  calcFinalRxnBnes(ATTRs, rxnBnes, charData.vision, infusion);
  return [ATTRs, hitBnes, rxnBnes, artSBnes];
}

// v2.7
// final ArtBonuses: Emblem of Severed Fate
// final ArtBuffs: Echoes of an Offering
// final wpBonuses: Homa, Jade Cutter, EL, Moonglow, Redhorn
